import { useMemo, useState, type CSSProperties } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, subMonths,
  format, isSameMonth, isToday, isSameDay,
} from 'date-fns'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { ChevronLeft, ChevronRight, Plus, MapPin, Users, Link2, X, CheckCircle2, Edit3, Trash2, ArrowUpRight } from 'lucide-react'
import { toast } from 'sonner'
import MeetingModal from '@/components/MeetingModal'
import { STATUS_META, isUrl, localDayKey, setMeetingStatus, deleteMeeting, type MeetingRow } from '@/lib/meetings'

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MAX_PILLS = 3

export default function SalesCalendar() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { profile, workspace } = useAuth()
  const meId = profile?.id ?? null
  const workspaceId = workspace?.id ?? null

  const [currentMonth, setCurrentMonth] = useState(() => startOfMonth(new Date()))
  const [selectedDay, setSelectedDay] = useState<Date | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<MeetingRow | null>(null)
  const [createStart, setCreateStart] = useState<string | null>(null)

  // The visible grid range (leading/trailing days of adjacent months included).
  const gridStart = startOfWeek(startOfMonth(currentMonth))
  const gridEnd = endOfWeek(endOfMonth(currentMonth))
  const days = useMemo(() => {
    const out: Date[] = []
    let d = gridStart
    while (d <= gridEnd) { out.push(d); d = addDays(d, 1) }
    return out
  }, [gridStart.getTime(), gridEnd.getTime()])

  // One query per visible range — served by the (workspace_id, start_time) index.
  const rangeKey = `${gridStart.toISOString()}_${gridEnd.toISOString()}`
  const { data: meetings = [], isLoading } = useQuery<MeetingRow[]>({
    queryKey: ['sales-calendar', workspaceId, rangeKey],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('sales_meetings')
        .select('*')
        .gte('start_time', gridStart.toISOString())
        .lt('start_time', addDays(gridEnd, 1).toISOString())
        .order('start_time', { ascending: true })
      if (error) throw error
      return (data ?? []) as MeetingRow[]
    },
    enabled: !!workspaceId,
  })

  // Resolve linked-deal titles for the agenda (the CP1 linkage; other types show a badge).
  const dealIds = useMemo(
    () => Array.from(new Set(meetings.filter(m => m.entity_type === 'deal' && m.entity_id).map(m => m.entity_id as string))),
    [meetings],
  )
  const { data: dealNames = {} } = useQuery<Record<string, string>>({
    queryKey: ['sales-calendar-deals', dealIds.sort().join(',')],
    queryFn: async () => {
      if (dealIds.length === 0) return {}
      const { data } = await supabase.from('deals').select('id, title').in('id', dealIds)
      const m: Record<string, string> = {}
      for (const d of (data ?? []) as { id: string; title: string }[]) m[d.id] = d.title
      return m
    },
    enabled: dealIds.length > 0,
    staleTime: 60_000,
  })

  const byDay = useMemo(() => {
    const map: Record<string, MeetingRow[]> = {}
    for (const m of meetings) {
      const k = localDayKey(m.start_time)
      ;(map[k] ??= []).push(m)
    }
    return map
  }, [meetings])

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['sales-calendar'] })
  }
  const statusMut = useMutation({
    mutationFn: (vars: { m: MeetingRow; status: 'completed' }) => setMeetingStatus(vars.m, vars.status, { workspaceId: workspaceId! }),
    onSuccess: invalidate,
    onError: (e: any) => toast.error(e?.message || 'Failed to update meeting'),
  })
  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteMeeting(id),
    onSuccess: invalidate,
    onError: (e: any) => toast.error(e?.message || 'Failed to delete meeting'),
  })

  const openCreate = (day?: Date) => {
    setEditing(null)
    // Prefill to 9:00am local on the clicked day (or now).
    const base = day ? new Date(day) : new Date()
    if (day) base.setHours(9, 0, 0, 0)
    setCreateStart(format(base, "yyyy-MM-dd'T'HH:mm"))
    setModalOpen(true)
  }
  const openEdit = (m: MeetingRow) => { setEditing(m); setCreateStart(null); setModalOpen(true) }

  const agendaMeetings = selectedDay ? (byDay[localDayKey(selectedDay.toISOString())] ?? []) : []

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--canvas)' }}>
      {/* Header */}
      <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexShrink: 0, background: 'var(--header-bg)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <h1 style={{ fontSize: 20, fontWeight: 800, color: 'var(--ink-primary)', margin: 0, letterSpacing: '-0.02em' }}>{format(currentMonth, 'MMMM yyyy')}</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <button onClick={() => setCurrentMonth(m => subMonths(m, 1))} aria-label="Previous month" style={navBtn}><ChevronLeft style={{ width: 16, height: 16 }} /></button>
            <button onClick={() => setCurrentMonth(startOfMonth(new Date()))} style={{ ...navBtn, width: 'auto', padding: '0 12px', fontSize: 12, fontWeight: 600 }}>Today</button>
            <button onClick={() => setCurrentMonth(m => addMonths(m, 1))} aria-label="Next month" style={navBtn}><ChevronRight style={{ width: 16, height: 16 }} /></button>
          </div>
        </div>
        <button onClick={() => openCreate()}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'var(--accent)', border: 'none', borderRadius: 9, color: 'var(--accent-ink)', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
          <Plus style={{ width: 14, height: 14 }} /> New meeting
        </button>
      </div>

      {/* Body: month grid + optional agenda panel */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
          {/* Weekday header */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', borderBottom: '1px solid var(--border-subtle)', flexShrink: 0 }}>
            {WEEKDAYS.map(d => (
              <div key={d} style={{ padding: '8px 12px', fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', textAlign: 'center' }}>{d}</div>
            ))}
          </div>
          {/* Day cells */}
          <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gridTemplateRows: `repeat(${days.length / 7}, minmax(110px, 1fr))` }}>
            {days.map((day, i) => {
              const inMonth = isSameMonth(day, currentMonth)
              const today = isToday(day)
              const dayMeetings = byDay[localDayKey(day.toISOString())] ?? []
              const isSelected = selectedDay && isSameDay(day, selectedDay)
              return (
                <div key={i} onClick={() => setSelectedDay(day)}
                  style={{ borderRight: '1px solid var(--border-subtle)', borderBottom: '1px solid var(--border-subtle)', padding: 6, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3, cursor: 'pointer', background: isSelected ? 'var(--hover-bg)' : 'transparent', opacity: inMonth ? 1 : 0.4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 12, fontWeight: today ? 800 : 500, color: today ? 'var(--accent-ink)' : 'var(--ink-secondary)', background: today ? 'var(--accent)' : 'transparent', width: 22, height: 22, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%' }}>{format(day, 'd')}</span>
                    <button onClick={e => { e.stopPropagation(); openCreate(day) }} aria-label="Add meeting" title="Add meeting"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', display: 'flex', padding: 1, opacity: 0.6 }}>
                      <Plus style={{ width: 12, height: 12 }} />
                    </button>
                  </div>
                  {dayMeetings.slice(0, MAX_PILLS).map(m => {
                    const meta = STATUS_META[m.status] || STATUS_META.scheduled
                    return (
                      <div key={m.id} title={m.title}
                        style={{ fontSize: 10, padding: '2px 5px', borderRadius: 5, color: meta.color, background: `color-mix(in srgb, ${meta.color} var(--status-tint-dim), transparent)`, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textDecoration: m.status === 'canceled' ? 'line-through' : 'none' }}>
                        {format(new Date(m.start_time), 'h:mm a')} · {m.title}
                      </div>
                    )
                  })}
                  {dayMeetings.length > MAX_PILLS && (
                    <span style={{ fontSize: 10, color: 'var(--ink-tertiary)', fontWeight: 600 }}>+{dayMeetings.length - MAX_PILLS} more</span>
                  )}
                </div>
              )
            })}
          </div>
          {isLoading && <div style={{ padding: 12, fontSize: 12, color: 'var(--ink-tertiary)' }}>Loading meetings…</div>}
        </div>

        {/* Day agenda panel */}
        {selectedDay && (
          <div style={{ flex: '0 0 340px', borderLeft: '1px solid var(--border-subtle)', overflow: 'auto', padding: '18px 18px', background: 'var(--surface-raised)', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 style={{ fontSize: 15, fontWeight: 800, color: 'var(--ink-primary)', margin: 0 }}>{format(selectedDay, 'EEEE, MMM d')}</h2>
              <button onClick={() => setSelectedDay(null)} aria-label="Close agenda" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', display: 'flex', padding: 2 }}><X style={{ width: 16, height: 16 }} /></button>
            </div>
            <button onClick={() => openCreate(selectedDay)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, alignSelf: 'flex-start', padding: '5px 10px', background: 'none', border: '1px dashed var(--border-default)', borderRadius: 8, color: 'var(--ink-tertiary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              <Plus style={{ width: 12, height: 12 }} /> New meeting
            </button>
            {agendaMeetings.length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>No meetings this day</p>
            ) : agendaMeetings.map(m => {
              const meta = STATUS_META[m.status] || STATUS_META.scheduled
              const dealName = m.entity_type === 'deal' && m.entity_id ? dealNames[m.entity_id] : null
              return (
                <div key={m.id} style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: 12, background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 700, color: 'var(--ink-primary)' }}>{m.title}</span>
                    <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 100, textTransform: 'uppercase', letterSpacing: '0.04em', color: meta.color, background: `color-mix(in srgb, ${meta.color} var(--status-tint-dim), transparent)` }}>{meta.label}</span>
                  </div>
                  <span style={{ fontSize: 12, color: 'var(--ink-secondary)' }}>{format(new Date(m.start_time), 'h:mm a')} – {format(new Date(m.end_time), 'h:mm a')}</span>
                  {dealName && (
                    <button onClick={() => navigate(`/app/deals/${m.entity_id}`)}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, alignSelf: 'flex-start', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent-text)', fontSize: 12, fontWeight: 600, padding: 0 }}>
                      {dealName} <ArrowUpRight style={{ width: 11, height: 11 }} />
                    </button>
                  )}
                  {m.location && (
                    <div style={{ fontSize: 12, color: 'var(--ink-tertiary)' }}>
                      {isUrl(m.location)
                        ? <a href={m.location} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--accent-text)', textDecoration: 'none' }}><Link2 style={{ width: 11, height: 11 }} /> Join meeting</a>
                        : <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><MapPin style={{ width: 11, height: 11 }} /> {m.location}</span>}
                    </div>
                  )}
                  {m.attendee_emails?.length > 0 && (
                    <div style={{ fontSize: 12, color: 'var(--ink-tertiary)', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Users style={{ width: 11, height: 11 }} /> {m.attendee_emails.join(', ')}</div>
                  )}
                  {m.notes && <p style={{ fontSize: 12, color: 'var(--ink-secondary)', margin: 0, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{m.notes}</p>}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingTop: 2 }}>
                    {m.status === 'scheduled' && (
                      <button onClick={() => statusMut.mutate({ m, status: 'completed' })}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--positive)', fontSize: 12, fontWeight: 600, padding: 0 }}><CheckCircle2 style={{ width: 13, height: 13 }} /> Complete</button>
                    )}
                    <button onClick={() => openEdit(m)}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', fontSize: 12, fontWeight: 600, padding: 0 }}><Edit3 style={{ width: 13, height: 13 }} /> Edit</button>
                    <button onClick={() => { if (window.confirm('Delete this meeting?')) deleteMut.mutate(m.id) }}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', fontSize: 12, fontWeight: 600, padding: 0 }}
                      onMouseEnter={e => { e.currentTarget.style.color = 'var(--danger)' }}
                      onMouseLeave={e => { e.currentTarget.style.color = 'var(--ink-tertiary)' }}><Trash2 style={{ width: 13, height: 13 }} /> Delete</button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      <MeetingModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={invalidate}
        workspaceId={workspaceId!}
        ownerId={meId}
        editing={editing}
        defaultStart={createStart}
      />
    </div>
  )
}

const navBtn: CSSProperties = {
  width: 30, height: 30, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 8,
  color: 'var(--ink-secondary)', cursor: 'pointer',
}
